import { findRegimen } from "@/data/meal-plan";
import type { DayOfWeek, DayPlan, WeekPlan } from "@/types/meal-plan-state";
import { DAYS_OF_WEEK } from "@/types/meal-plan-state";
import { isMealEmpty } from "./copy-meal-notes";
import { hasEatenMarks } from "./owner-eaten";
import {
  dayMealIndex,
  mealMatch,
  noteAt,
  sameNote,
  saveComidasNote,
  type ComidasMeal,
  type MealMatch,
} from "./comidas-day";

/**
 * "También para": what I write on my Comidas also lands in a friend's week,
 * each note filling their own meal (their plan, their portions), as if they
 * had typed it. Only the note travels; foods picked by hand stay with
 * whoever picked them.
 *
 * The sync rule keeps it safe: a friend's meal is written only while it is
 * empty or still holds my previous note, and has no "comido" ticks. A meal
 * they planned differently is theirs: it is kept, and the planner can
 * replace it on purpose (`force`). Writes go one hop: what lands in a
 * friend's week is never shared onward.
 */

const MEALS: ComidasMeal[] = ["lunch", "dinner"];

/** A lunch or dinner note of mine that changed: `prev` → `next` (trimmed, "" for none). */
export interface NoteChange {
  meal: ComidasMeal;
  prev: string;
  next: string;
}

export interface ShareAction {
  day: DayOfWeek;
  changes: NoteChange[];
  /** Write the meals the sync rule protects too ("Reemplazar"). */
  force?: boolean;
}

/** The lunch / dinner notes that differ between my day before and after an edit. */
export function noteChanges(before: DayPlan, after: DayPlan): NoteChange[] {
  const changes: NoteChange[] = [];
  for (const meal of MEALS) {
    const bi = dayMealIndex(before, meal);
    const ai = dayMealIndex(after, meal);
    const prev = bi === null ? "" : noteAt(before, bi);
    const next = ai === null ? "" : noteAt(after, ai);
    if (prev !== next) changes.push({ meal, prev, next });
  }
  return changes;
}

export type FriendMealOutcome =
  | { kind: "written" }
  /** Already holds the new note (or, clearing, is already empty). */
  | { kind: "already" }
  /** Their day has no day type. */
  | { kind: "no-day" }
  /** Their day type has no such meal. */
  | { kind: "no-meal" }
  /** Theirs is something else: their own note, foods picked by hand, or "comido" ticks. */
  | { kind: "kept"; reason: "note" | "picks" | "eaten"; note: string };

export type FriendOutcomes = Partial<Record<ComidasMeal, FriendMealOutcome>>;

/**
 * Apply my note changes to the friend's same day, per the sync rule. When
 * both meals are written with the same note, they're filled as a "same"
 * day (dinner like lunch), exactly as typing it on their Comidas. Pure.
 */
export function applyShareAction(friendDay: DayPlan, action: ShareAction): { day: DayPlan; outcomes: FriendOutcomes } {
  const outcomes: FriendOutcomes = {};
  const regimen = findRegimen(friendDay.regimenId);
  if (!regimen || !friendDay.meals) {
    for (const c of action.changes) outcomes[c.meal] = { kind: "no-day" };
    return { day: friendDay, outcomes };
  }

  const writes: NoteChange[] = [];
  for (const change of action.changes) {
    const index = dayMealIndex(friendDay, change.meal);
    if (index === null) {
      outcomes[change.meal] = { kind: "no-meal" };
      continue;
    }
    const theirNote = noteAt(friendDay, index);
    const empty = isMealEmpty(friendDay, regimen.meals[index]!, index);
    const synced = empty || (change.prev !== "" && sameNote(theirNote, change.prev));
    const eaten = hasEatenMarks(friendDay.meals, [index]);
    if (change.next ? sameNote(theirNote, change.next) : empty) {
      outcomes[change.meal] = { kind: "already" };
    } else if (action.force || (synced && !eaten)) {
      writes.push(change);
      outcomes[change.meal] = { kind: "written" };
    } else if (!synced && !change.next) {
      // Clearing my dish never touches a meal that was never mine: nothing to report.
      outcomes[change.meal] = { kind: "already" };
    } else {
      outcomes[change.meal] = { kind: "kept", reason: eaten ? "eaten" : theirNote ? "note" : "picks", note: theirNote };
    }
  }

  const lunch = writes.find((w) => w.meal === "lunch");
  const dinner = writes.find((w) => w.meal === "dinner");
  let day = friendDay;
  if (lunch && dinner && lunch.next === dinner.next) {
    day = saveComidasNote(day, "both", lunch.next);
  } else {
    for (const w of writes) day = saveComidasNote(day, w.meal, w.next);
  }
  return { day, outcomes };
}

/** How a friend's meal compares to mine, for the row's chip. */
export type FriendMealStatus =
  /** I have no dish for this meal: nothing to compare. */
  | { kind: "none" }
  | { kind: "no-day" }
  | { kind: "no-meal" }
  /** Same dish; `match` is how it filled *their* plan. */
  | { kind: "synced"; match: MealMatch }
  /** I have a dish, their meal is empty. */
  | { kind: "missing" }
  /** Their meal holds something else (`note` "" when only foods picked by hand). */
  | { kind: "different"; note: string };

export function friendMealStatus(myDay: DayPlan, friendDay: DayPlan, meal: ComidasMeal): FriendMealStatus {
  const mine = dayMealIndex(myDay, meal);
  const myNote = mine === null ? "" : noteAt(myDay, mine);
  if (!myNote) return { kind: "none" };
  const regimen = findRegimen(friendDay.regimenId);
  if (!regimen || !friendDay.meals) return { kind: "no-day" };
  const index = dayMealIndex(friendDay, meal);
  if (index === null) return { kind: "no-meal" };
  const theirMeal = regimen.meals[index]!;
  const theirNote = noteAt(friendDay, index);
  if (sameNote(theirNote, myNote)) {
    return {
      kind: "synced",
      match: mealMatch(theirNote, theirMeal, friendDay.mealMenus?.[index], friendDay.meals[index]!),
    };
  }
  if (isMealEmpty(friendDay, theirMeal, index)) return { kind: "missing" };
  return { kind: "different", note: theirNote };
}

/** One badge per friend and day: the most important of its two meals. */
export type FriendChipKind = "ok" | "warn" | "error" | "different" | "missing" | "unavailable";

const CHIP_PRIORITY: FriendChipKind[] = ["error", "warn", "different", "missing", "unavailable", "ok"];

export function chipKind(status: FriendMealStatus): FriendChipKind | null {
  switch (status.kind) {
    case "none":
      return null;
    case "no-day":
    case "no-meal":
      return "unavailable";
    case "missing":
      return "missing";
    case "different":
      return "different";
    case "synced":
      return status.match.status === "ok" || status.match.status === "empty"
        ? "ok"
        : status.match.status === "partial"
          ? "warn"
          : "error";
  }
}

export function dayChipKind(statuses: FriendMealStatus[]): FriendChipKind | null {
  const kinds = new Set(statuses.map(chipKind).filter((k): k is FriendChipKind => k !== null));
  return CHIP_PRIORITY.find((k) => kinds.has(k)) ?? null;
}

/**
 * "Copiar": for every day where I have a dish and the friend's meal is
 * empty, the action that fills it (the rule writes empty meals).
 */
export function missingDayActions(myWeek: WeekPlan, friendWeek: WeekPlan): ShareAction[] {
  const actions: ShareAction[] = [];
  for (const { id: day } of DAYS_OF_WEEK) {
    const changes: NoteChange[] = [];
    for (const meal of MEALS) {
      if (friendMealStatus(myWeek[day], friendWeek[day], meal).kind !== "missing") continue;
      const index = dayMealIndex(myWeek[day], meal)!;
      changes.push({ meal, prev: "", next: noteAt(myWeek[day], index) });
    }
    if (changes.length > 0) actions.push({ day, changes });
  }
  return actions;
}
